/**
 * PipeFlowCalculatorPage.tsx — Pipe flow + centrifugal-pump + tank-fill calculator
 *
 * Solves the operating point of a centrifugal pump against a multi-segment
 * pipe system: pump → horizontal pipe → vertical lift → header (2× diameter)
 * → horizontal to tank. Reports time-to-fill the receiving tank, per-segment
 * velocity, Reynolds, friction factor, head loss, and total system head.
 *
 * References:
 *   Colebrook-White (1939) — implicit friction factor for turbulent flow
 *   Swamee & Jain (1976) — explicit approximation to Colebrook-White
 *   Cengel & Cimbala (2014) — Fluid Mechanics, McGraw-Hill
 *   Karassik (2008) — Pump Handbook
 *   Affinity laws, parabolic pump curve approximation H(Q) = H_0 · (1 − (Q/Q_max)²)
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Calculator, ArrowLeft, Info, Droplet, Wind, Gauge,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';

const G = 9.81;

// ─── Fluid library ───────────────────────────────────────────────────────────
type FluidType = 'water_20' | 'water_50' | 'air_20' | 'air_60' | 'custom';
interface Fluid { id: FluidType; label: string; rho: number; mu: number; phase: 'liquid' | 'gas'; }
const FLUIDS: Fluid[] = [
  { id: 'water_20', label: 'Water @ 20 °C',  rho: 998.2, mu: 1.002e-3, phase: 'liquid' },
  { id: 'water_50', label: 'Water @ 50 °C',  rho: 988.1, mu: 0.547e-3, phase: 'liquid' },
  { id: 'air_20',   label: 'Air @ 20 °C',     rho: 1.204, mu: 1.825e-5, phase: 'gas' },
  { id: 'air_60',   label: 'Air @ 60 °C',     rho: 1.060, mu: 1.997e-5, phase: 'gas' },
  { id: 'custom',   label: 'Custom',          rho: 998.2, mu: 1.002e-3, phase: 'liquid' },
];

// ─── Pipe roughness library (Moody) ──────────────────────────────────────────
const ROUGHNESS_UM: Record<string, number> = {
  hdpe: 1.5,       // 0.0015 mm
  pvc: 1.5,
  smooth_steel: 15,
  cast_iron: 250,
  concrete: 1500,
};

// ─── Pump catalogue ──────────────────────────────────────────────────────────
type PumpType = 'p_1kw' | 'p_2kw' | 'p_5kw' | 'p_custom';
interface PumpDef { id: PumpType; label: string; powerKW: number; H_max_m: number; Q_max_m3h: number; eff: number; }
// Typical small centrifugal pump performance envelopes (parabolic H-Q)
const PUMPS: PumpDef[] = [
  { id: 'p_1kw', label: '1 kW centrifugal',  powerKW: 1.0, H_max_m: 14, Q_max_m3h:  8, eff: 0.55 },
  { id: 'p_2kw', label: '2 kW centrifugal',  powerKW: 2.0, H_max_m: 22, Q_max_m3h: 16, eff: 0.62 },
  { id: 'p_5kw', label: '5 kW centrifugal',  powerKW: 5.0, H_max_m: 35, Q_max_m3h: 36, eff: 0.70 },
  { id: 'p_custom', label: 'Custom Q rating', powerKW: 0,   H_max_m: 20, Q_max_m3h: 12, eff: 0.65 },
];

// ─── Friction factor — Swamee-Jain explicit approximation to Colebrook ──────
function frictionFactor(Re: number, eps_over_D: number): number {
  if (Re < 2300) return 64 / Math.max(1, Re);           // laminar
  if (Re < 4000) {                                       // transitional — linear interpolate
    const fLam = 64 / 2300;
    const fTurb = swameeJain(4000, eps_over_D);
    const t = (Re - 2300) / (4000 - 2300);
    return fLam + (fTurb - fLam) * t;
  }
  return swameeJain(Re, eps_over_D);
}

function swameeJain(Re: number, eps_over_D: number): number {
  const term = eps_over_D / 3.7 + 5.74 / Math.pow(Re, 0.9);
  const denom = Math.log10(term);
  return 0.25 / (denom * denom);
}

// ─── Segment hydraulics ──────────────────────────────────────────────────────
interface SegmentResult {
  label: string;
  type: 'horizontal' | 'vertical' | 'horizontal_header';
  D_mm: number;
  L_m: number;
  elevation_m: number;       // +ve = rise
  area_m2: number;
  velocity_m_s: number;
  Re: number;
  f: number;
  head_loss_m: number;
  pressure_drop_Pa: number;
}

function segmentHydraulics(
  Q_m3s: number,
  D_mm: number,
  L_m: number,
  elevation_m: number,
  rho: number,
  mu: number,
  eps_um: number,
  label: string,
  type: SegmentResult['type'],
): SegmentResult {
  const D_m = D_mm / 1000;
  const area_m2 = Math.PI * (D_m / 2) ** 2;
  const v = Q_m3s / Math.max(1e-9, area_m2);
  const Re = rho * Math.abs(v) * D_m / Math.max(1e-12, mu);
  const eps_over_D = (eps_um / 1e6) / Math.max(1e-9, D_m);
  const f = frictionFactor(Re, eps_over_D);
  const head_loss_m = f * (L_m / Math.max(1e-9, D_m)) * v * v / (2 * G);
  const pressure_drop_Pa = head_loss_m * rho * G;
  return { label, type, D_mm, L_m, elevation_m, area_m2, velocity_m_s: v, Re, f, head_loss_m, pressure_drop_Pa };
}

// ─── Solve pump operating point ──────────────────────────────────────────────
/**
 * Pump curve: H_pump(Q) = H_max × (1 − (Q/Q_max)²)
 * System curve: H_sys(Q) = H_static + Σ h_f(Q)
 * Operating point: bisection search for Q where H_pump(Q) = H_sys(Q)
 */
function solveOperatingPoint(
  pump: PumpDef,
  staticHead_m: number,
  segments: { D_mm: number; L_m: number; elev: number }[],
  rho: number, mu: number, eps_um: number,
): { Q_m3s: number; iterations: number } {
  const H_pump = (Q: number) => pump.H_max_m * (1 - Math.pow(Q / (pump.Q_max_m3h / 3600), 2));
  const H_sys = (Q: number) => {
    let H_loss = 0;
    for (const s of segments) {
      const r = segmentHydraulics(Q, s.D_mm, s.L_m, s.elev, rho, mu, eps_um, '', 'horizontal');
      H_loss += r.head_loss_m;
    }
    return staticHead_m + H_loss;
  };
  // Bisection: find Q in [0, Q_max] where H_pump - H_sys = 0
  let lo = 0;
  let hi = pump.Q_max_m3h / 3600;
  let mid = (lo + hi) / 2;
  let iter = 0;
  for (; iter < 80; iter++) {
    mid = (lo + hi) / 2;
    const diff = H_pump(mid) - H_sys(mid);
    if (Math.abs(diff) < 1e-4) break;
    if (diff > 0) lo = mid; else hi = mid;
    if ((hi - lo) < 1e-9) break;
  }
  return { Q_m3s: Math.max(0, mid), iterations: iter };
}

function fmt(n: number, d = 2): string {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtTime(seconds: number): string {
  if (!isFinite(seconds) || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const mins = seconds / 60;
  if (mins < 60) return `${mins.toFixed(1)} min`;
  const hrs = mins / 60;
  if (hrs < 24) return `${hrs.toFixed(2)} h`;
  return `${(hrs / 24).toFixed(2)} d`;
}

export default function PipeFlowCalculatorPage() {
  const navigate = useNavigate();

  // ── Fluid inputs ─────────────────────────────────────────────────────────
  const [fluidId, setFluidId] = useState<FluidType>('water_20');
  const [customRho, setCustomRho] = useState(998.2);
  const [customMu, setCustomMu] = useState(0.001);

  const fluid = useMemo(() => {
    const base = FLUIDS.find(f => f.id === fluidId) ?? FLUIDS[0];
    if (fluidId === 'custom') return { ...base, rho: customRho, mu: customMu };
    return base;
  }, [fluidId, customRho, customMu]);

  // ── Pump inputs ──────────────────────────────────────────────────────────
  const [pumpId, setPumpId] = useState<PumpType>('p_2kw');
  const [customQRating, setCustomQRating] = useState(12);     // m³/h at rated head
  const [customHRating, setCustomHRating] = useState(15);

  const pump = useMemo(() => {
    const base = PUMPS.find(p => p.id === pumpId) ?? PUMPS[1];
    if (pumpId === 'p_custom') {
      // Build a parabolic pump curve from the customQRating & customHRating
      return { ...base, Q_max_m3h: customQRating * 1.5, H_max_m: customHRating * 1.7 };
    }
    return base;
  }, [pumpId, customQRating, customHRating]);

  // ── Pipe system ──────────────────────────────────────────────────────────
  const [pipeMaterial, setPipeMaterial] = useState<keyof typeof ROUGHNESS_UM>('hdpe');
  const eps_um = ROUGHNESS_UM[pipeMaterial];

  const [D1_mm, setD1_mm] = useState(50);          // first pipe diameter
  const [L1_h_m, setL1_h_m] = useState(8);          // horizontal length from pump
  const [L_v_m,  setL_v_m]  = useState(4);          // vertical lift
  const [L_header_h_m, setL_header_h_m] = useState(12);   // horizontal on header to tank
  const headerD_mm = D1_mm * 2;                      // header is 2× diameter as requested

  // ── Tank ─────────────────────────────────────────────────────────────────
  const [tankL_m, setTankL_m] = useState(3.0);
  const [tankW_m, setTankW_m] = useState(2.0);
  const [tankH_m, setTankH_m] = useState(2.5);
  const tankVolume_m3 = tankL_m * tankW_m * tankH_m;

  // Static head = vertical lift (taken as elevation between pump suction and tank inlet)
  const staticHead_m = L_v_m;

  // ── Solve operating point ────────────────────────────────────────────────
  const op = useMemo(() => {
    return solveOperatingPoint(
      pump,
      staticHead_m,
      [
        { D_mm: D1_mm,      L_m: L1_h_m,        elev: 0 },
        { D_mm: D1_mm,      L_m: L_v_m,          elev: L_v_m },
        { D_mm: headerD_mm, L_m: L_header_h_m,   elev: 0 },
      ],
      fluid.rho, fluid.mu, eps_um,
    );
  }, [pump, staticHead_m, D1_mm, L1_h_m, L_v_m, headerD_mm, L_header_h_m, fluid.rho, fluid.mu, eps_um]);

  const Q_m3s = op.Q_m3s;
  const Q_m3h = Q_m3s * 3600;
  const Q_Lmin = Q_m3s * 60_000;

  // Per-segment results
  const segments = useMemo(() => [
    segmentHydraulics(Q_m3s, D1_mm,      L1_h_m,      0,     fluid.rho, fluid.mu, eps_um, '1. Pump → header (horizontal)', 'horizontal'),
    segmentHydraulics(Q_m3s, D1_mm,      L_v_m,        L_v_m, fluid.rho, fluid.mu, eps_um, '2. Vertical lift',                'vertical'),
    segmentHydraulics(Q_m3s, headerD_mm, L_header_h_m, 0,     fluid.rho, fluid.mu, eps_um, `3. Header → tank (Ø${headerD_mm} mm)`, 'horizontal_header'),
  ], [Q_m3s, D1_mm, L1_h_m, L_v_m, L_header_h_m, headerD_mm, fluid.rho, fluid.mu, eps_um]);

  const totalFrictionLoss = segments.reduce((s, x) => s + x.head_loss_m, 0);
  const totalHead = staticHead_m + totalFrictionLoss;
  const hydraulicPower_W = fluid.rho * G * Q_m3s * totalHead;
  const shaftPower_W = hydraulicPower_W / Math.max(0.01, pump.eff);

  const timeToFill_s = Q_m3s > 0 ? tankVolume_m3 / Q_m3s : Infinity;

  // ─── Render visual ───────────────────────────────────────────────────────
  const svgW = 760, svgH = 320;
  // Geometry scaling
  const pumpX = 50, pumpY = 240;
  const seg1End_x = 220;
  const vertTop_y = 100;
  const headerEnd_x = 600;
  const tankX = 620, tankY = 80;
  const tankWidthPx = 110, tankHeightPx = 160;
  const pipeStroke_D1 = Math.max(4, Math.min(16, D1_mm / 6));
  const pipeStroke_header = Math.max(6, Math.min(28, headerD_mm / 6));
  // Tank fill level rises with time — for display purposes, show 60% filled instantaneously
  const fillFrac = 0.6;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Calculator}
        title="Pipe Flow & Centrifugal Pump Calculator"
        subtitle="Pump → horizontal → vertical → header (2× Ø) → tank. Per-segment hydraulics, pump operating point, tank-fill time."
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Headline results */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-rb-brand/40 bg-rb-brand/10 p-4">
          <div className="text-xs uppercase tracking-wider text-rb-brand">Operating flow Q</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(Q_m3h, 2)} m³/h</div>
          <div className="text-xs text-slate-400 mt-1">{fmt(Q_Lmin, 1)} L/min · {fmt(Q_m3s * 1000, 2)} L/s</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Time to fill tank</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{fmtTime(timeToFill_s)}</div>
          <div className="text-xs text-slate-400 mt-1">V = {fmt(tankVolume_m3, 2)} m³</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Total system head</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(totalHead, 2)} m</div>
          <div className="text-xs text-slate-400 mt-1">static {fmt(staticHead_m, 1)} + friction {fmt(totalFrictionLoss, 2)}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Power consumed</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(shaftPower_W / 1000, 2)} kW</div>
          <div className="text-xs text-slate-400 mt-1">hydraulic {fmt(hydraulicPower_W / 1000, 2)} kW · η {(pump.eff * 100).toFixed(0)}%</div>
        </div>
      </div>

      {/* System visual */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">System layout</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {/* ground line */}
          <line x1={10} y1={290} x2={svgW - 10} y2={290} stroke="#475569" strokeWidth={1} strokeDasharray="3 2" />
          <text x={15} y={302} fontSize={9} fill="#64748b">datum</text>

          {/* Pump */}
          <g>
            <circle cx={pumpX} cy={pumpY} r={20} fill="#1e40af" stroke="#3b82f6" strokeWidth={2} />
            <text x={pumpX} y={pumpY + 4} textAnchor="middle" fontSize={11} fill="#fff" fontWeight="bold">P</text>
            <text x={pumpX} y={pumpY + 38} textAnchor="middle" fontSize={9} fill="#94a3b8">
              {pump.label}
            </text>
            <text x={pumpX} y={pumpY + 49} textAnchor="middle" fontSize={8} fill="#64748b">
              {fmt(pump.powerKW, 1)} kW
            </text>
          </g>

          {/* Segment 1: horizontal from pump */}
          <line x1={pumpX + 20} y1={pumpY} x2={seg1End_x} y2={pumpY}
            stroke="#06b6d4" strokeWidth={pipeStroke_D1} strokeLinecap="round" />
          <text x={(pumpX + seg1End_x) / 2} y={pumpY - pipeStroke_D1 - 6} textAnchor="middle" fontSize={10} fill="#06b6d4">
            Ø{D1_mm} · {L1_h_m} m
          </text>
          <text x={(pumpX + seg1End_x) / 2} y={pumpY + pipeStroke_D1 + 14} textAnchor="middle" fontSize={9} fill="#94a3b8">
            v = {fmt(segments[0].velocity_m_s, 2)} m/s · Re {segments[0].Re.toExponential(1)}
          </text>

          {/* Elbow */}
          <circle cx={seg1End_x} cy={pumpY} r={pipeStroke_D1 / 2 + 2} fill="#06b6d4" />

          {/* Segment 2: vertical */}
          <line x1={seg1End_x} y1={pumpY} x2={seg1End_x} y2={vertTop_y}
            stroke="#06b6d4" strokeWidth={pipeStroke_D1} strokeLinecap="round" />
          <text x={seg1End_x + pipeStroke_D1 + 6} y={(pumpY + vertTop_y) / 2} fontSize={10} fill="#06b6d4">
            ↑ {L_v_m} m
          </text>
          <text x={seg1End_x + pipeStroke_D1 + 6} y={(pumpY + vertTop_y) / 2 + 12} fontSize={9} fill="#94a3b8">
            v = {fmt(segments[1].velocity_m_s, 2)} m/s
          </text>

          {/* Header transition (reducer/expander) — junction */}
          <polygon points={`${seg1End_x - pipeStroke_D1 / 2},${vertTop_y} ${seg1End_x + pipeStroke_D1 / 2},${vertTop_y} ${seg1End_x + pipeStroke_header / 2},${vertTop_y - 8} ${seg1End_x - pipeStroke_header / 2},${vertTop_y - 8}`}
            fill="#fbbf24" stroke="#0a1628" strokeWidth={0.5} />
          <text x={seg1End_x - pipeStroke_header / 2 - 5} y={vertTop_y - 12} textAnchor="end" fontSize={9} fill="#fbbf24">expander 2×</text>

          {/* Segment 3: header horizontal */}
          <line x1={seg1End_x} y1={vertTop_y - 8} x2={headerEnd_x} y2={vertTop_y - 8}
            stroke="#a16207" strokeWidth={pipeStroke_header} strokeLinecap="round" />
          <text x={(seg1End_x + headerEnd_x) / 2} y={vertTop_y - pipeStroke_header - 6} textAnchor="middle" fontSize={10} fill="#fbbf24">
            HEADER Ø{headerD_mm} · {L_header_h_m} m
          </text>
          <text x={(seg1End_x + headerEnd_x) / 2} y={vertTop_y + 4} textAnchor="middle" fontSize={9} fill="#94a3b8">
            v = {fmt(segments[2].velocity_m_s, 2)} m/s · ¼ velocity of pipe 1
          </text>

          {/* Tank */}
          <g>
            <rect x={tankX} y={tankY} width={tankWidthPx} height={tankHeightPx}
              fill="none" stroke="#94a3b8" strokeWidth={2} />
            {/* Fill level */}
            <rect x={tankX} y={tankY + tankHeightPx * (1 - fillFrac)} width={tankWidthPx} height={tankHeightPx * fillFrac}
              fill="rgba(56, 189, 248, 0.35)" stroke="#38bdf8" strokeWidth={1} />
            {/* Inlet from header */}
            <line x1={headerEnd_x} y1={vertTop_y - 8} x2={tankX} y2={vertTop_y - 8}
              stroke="#a16207" strokeWidth={pipeStroke_header} strokeLinecap="round" />
            {/* Outlet trickle into tank */}
            <line x1={tankX + 5} y1={vertTop_y - 4} x2={tankX + 5} y2={tankY + tankHeightPx * (1 - fillFrac) + 4}
              stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="2 2" />
            <text x={tankX + tankWidthPx / 2} y={tankY - 6} textAnchor="middle" fontSize={10} fill="#cbd5e1">
              Tank {tankL_m}×{tankW_m}×{tankH_m} m
            </text>
            <text x={tankX + tankWidthPx / 2} y={tankY + tankHeightPx + 14} textAnchor="middle" fontSize={9} fill="#94a3b8">
              V = {fmt(tankVolume_m3, 2)} m³
            </text>
          </g>

          {/* Flow direction arrows */}
          {[120, 280, 470].map((x, i) => (
            <polygon key={i} points={`${x},${pumpY - 1} ${x + 6},${pumpY + 3} ${x},${pumpY + 7}`} fill="#38bdf8" opacity={0.85} />
          ))}
        </svg>
        <div className="text-[10px] text-slate-500 mt-2 flex flex-wrap gap-3">
          <span><span className="inline-block w-3 h-1.5 bg-cyan-500 align-middle mr-1" />Pipe 1 (Ø{D1_mm} mm)</span>
          <span><span className="inline-block w-3 h-2 bg-amber-700 align-middle mr-1" />Header (Ø{headerD_mm} mm, 2× area = 4× cross-section)</span>
          <span><span className="inline-block w-2 h-2 bg-blue-700 rounded-full align-middle mr-1" />Pump</span>
          <span><span className="inline-block w-3 h-2 border border-slate-400 align-middle mr-1" />Tank</span>
        </div>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Fluid */}
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <Droplet className="w-4 h-4 text-sky-300" /> Fluid
          </div>
          <select value={fluidId} onChange={e => setFluidId(e.target.value as FluidType)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100 mb-2">
            {FLUIDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          {fluidId === 'custom' && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <label className="text-slate-400 block mb-0.5">ρ (kg/m³)</label>
                <input type="number" step={1} value={customRho}
                  onChange={e => setCustomRho(Number(e.target.value))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
              </div>
              <div>
                <label className="text-slate-400 block mb-0.5">μ (Pa·s)</label>
                <input type="number" step={1e-5} value={customMu}
                  onChange={e => setCustomMu(Number(e.target.value))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
              </div>
            </div>
          )}
          <div className="text-xs text-slate-400 mt-2 space-y-0.5">
            <div>ρ = <span className="text-slate-100">{fmt(fluid.rho, 1)}</span> kg/m³</div>
            <div>μ = <span className="text-slate-100">{fluid.mu.toExponential(2)}</span> Pa·s</div>
            <div>ν = <span className="text-slate-100">{(fluid.mu / fluid.rho).toExponential(2)}</span> m²/s</div>
            <div className="text-[10px] text-slate-500 italic mt-1">
              {fluid.phase === 'gas' ? <>{'Gas — compressibility ignored for low velocities (<0.3 Ma)'}</> : 'Incompressible liquid'}
            </div>
          </div>
        </div>

        {/* Pump */}
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-emerald-300" /> Pump
          </div>
          <select value={pumpId} onChange={e => setPumpId(e.target.value as PumpType)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100 mb-2">
            {PUMPS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {pumpId === 'p_custom' && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <label className="text-slate-400 block mb-0.5">Rated Q (m³/h)</label>
                <input type="number" step={0.5} value={customQRating}
                  onChange={e => setCustomQRating(Number(e.target.value))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
              </div>
              <div>
                <label className="text-slate-400 block mb-0.5">Rated H (m)</label>
                <input type="number" step={0.5} value={customHRating}
                  onChange={e => setCustomHRating(Number(e.target.value))}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
              </div>
            </div>
          )}
          <div className="text-xs text-slate-400 mt-2 space-y-0.5">
            <div>H_max (shut-off): <span className="text-slate-100">{fmt(pump.H_max_m, 1)}</span> m</div>
            <div>Q_max (free flow): <span className="text-slate-100">{fmt(pump.Q_max_m3h, 1)}</span> m³/h</div>
            <div>η: <span className="text-slate-100">{(pump.eff * 100).toFixed(0)}%</span></div>
            <div className="text-[10px] text-slate-500 italic mt-1">
              Parabolic curve H(Q) = H₀·(1 − (Q/Q_max)²)
            </div>
          </div>
        </div>

        {/* Pipe system */}
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <Wind className="w-4 h-4 text-amber-300" /> Pipe system
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="col-span-2">
              <label className="text-slate-400 block mb-0.5">Material</label>
              <select value={pipeMaterial} onChange={e => setPipeMaterial(e.target.value as keyof typeof ROUGHNESS_UM)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100">
                <option value="hdpe">HDPE (ε = 1.5 µm)</option>
                <option value="pvc">PVC (ε = 1.5 µm)</option>
                <option value="smooth_steel">Smooth steel (ε = 15 µm)</option>
                <option value="cast_iron">Cast iron (ε = 250 µm)</option>
                <option value="concrete">Concrete (ε = 1500 µm)</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 block mb-0.5">Pipe Ø D₁ (mm)</label>
              <input type="number" step={5} value={D1_mm}
                onChange={e => setD1_mm(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
            </div>
            <div>
              <label className="text-slate-400 block mb-0.5">Header Ø (auto)</label>
              <input type="number" value={headerD_mm} disabled
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-500" />
            </div>
            <div>
              <label className="text-slate-400 block mb-0.5">L1 horizontal (m)</label>
              <input type="number" step={0.5} value={L1_h_m}
                onChange={e => setL1_h_m(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
            </div>
            <div>
              <label className="text-slate-400 block mb-0.5">Vertical lift (m)</label>
              <input type="number" step={0.5} value={L_v_m}
                onChange={e => setL_v_m(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
            </div>
            <div className="col-span-2">
              <label className="text-slate-400 block mb-0.5">Header horizontal to tank (m)</label>
              <input type="number" step={0.5} value={L_header_h_m}
                onChange={e => setL_header_h_m(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
            </div>
          </div>
        </div>
      </div>

      {/* Tank inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Tank dimensions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Length L (m)</label>
            <input type="number" step={0.1} value={tankL_m}
              onChange={e => setTankL_m(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Width W (m)</label>
            <input type="number" step={0.1} value={tankW_m}
              onChange={e => setTankW_m(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Height H (m)</label>
            <input type="number" step={0.1} value={tankH_m}
              onChange={e => setTankH_m(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">Volume</div>
            <div className="text-lg font-bold text-emerald-300">{fmt(tankVolume_m3, 2)} m³</div>
            <div className="text-[10px] text-slate-500">{(tankVolume_m3 * 1000).toFixed(0)} L</div>
          </div>
        </div>
      </div>

      {/* Per-segment hydraulics table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Per-segment hydraulics</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Segment</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Ø (mm)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">L (m)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">A (m²)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">v (m/s)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Re</th>
                <th className="px-2 py-1.5 text-right text-slate-300">f (Darcy)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">h_f (m)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Δp (kPa)</th>
              </tr>
            </thead>
            <tbody>
              {segments.map((s, i) => (
                <tr key={i} className={`border-b border-slate-800 ${s.type === 'horizontal_header' ? 'bg-amber-900/10' : ''}`}>
                  <td className="px-2 py-1 text-slate-100">{s.label}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.D_mm}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.L_m.toFixed(1)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.area_m2.toExponential(2)}</td>
                  <td className="px-2 py-1 text-right text-slate-100 font-semibold">{fmt(s.velocity_m_s, 2)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.Re.toExponential(1)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(s.f, 4)}</td>
                  <td className="px-2 py-1 text-right text-amber-300 tabular-nums">{fmt(s.head_loss_m, 3)}</td>
                  <td className="px-2 py-1 text-right text-amber-300 tabular-nums">{fmt(s.pressure_drop_Pa / 1000, 2)}</td>
                </tr>
              ))}
              <tr className="bg-slate-800 font-semibold">
                <td className="px-2 py-1.5 text-slate-100">Static head (vertical lift)</td>
                <td colSpan={6}></td>
                <td className="px-2 py-1.5 text-right text-slate-100">{fmt(staticHead_m, 2)}</td>
                <td className="px-2 py-1.5 text-right text-slate-100">{fmt(staticHead_m * fluid.rho * G / 1000, 2)}</td>
              </tr>
              <tr className="bg-emerald-900/20 font-semibold">
                <td className="px-2 py-1.5 text-slate-100">Total system head (static + friction)</td>
                <td colSpan={6}></td>
                <td className="px-2 py-1.5 text-right text-emerald-300">{fmt(totalHead, 2)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-300">{fmt(totalHead * fluid.rho * G / 1000, 2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Methodology */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            <strong className="text-sky-300">Per-segment hydraulics</strong> uses the Darcy-Weisbach equation
            h_f = f·(L/D)·v²/(2g). The friction factor f is obtained from Colebrook-White via the
            Swamee-Jain 1976 explicit approximation valid for Re &gt; 4000; laminar regime uses f = 64/Re;
            transitional regime (2300–4000) is linearly interpolated.
          </p>
          <p>
            <strong className="text-emerald-300">Pump operating point</strong> is found by bisection on the
            quadratic intersection of the parabolic pump curve H_pump(Q) = H₀·(1 − (Q/Q_max)²) and the
            system curve H_sys(Q) = H_static + Σ h_f(Q). Pump efficiency η is applied to derive shaft power
            from hydraulic power.
          </p>
          <p>
            <strong className="text-amber-300">Header transition</strong> is modelled as a 2×-diameter
            expansion. Since cross-sectional area scales with D², the header has 4× the area of the upstream
            pipe → velocity drops to ¼ of the upstream value → friction loss per metre drops by ~½⁵ ≈ 1/32×.
            That's why headers reduce friction losses dramatically.
          </p>
          <p>
            <strong className="text-violet-300">Tank fill time</strong> is V_tank / Q_operating where Q is
            the operating-point flow. For very small pumps or very long pipes the operating point can fall
            close to shut-off — the page will report a very long fill time, indicating that the pump is
            undersized for the system.
          </p>
          <p className="italic text-slate-500">
            References: Colebrook 1939; Swamee &amp; Jain 1976; Cengel &amp; Cimbala 2014 — Fluid Mechanics; Karassik 2008 — Pump Handbook.
          </p>
        </div>
      </div>
    </div>
  );
}
